/**
 * Shared table / datagrid behaviour for both grid systems (Ublaboo + DataTables).
 *
 * Rules this module exists to keep:
 *
 *  - Everything is registered once at parse time and delegated on `document`. Ublaboo
 *    redraws its whole `<table>` through a Nette snippet and DataTables rebuilds its
 *    header on every draw, so a handler bound to an element dies on the first redraw.
 *  - Ublaboo emits no DOM events of its own (`datagrid-after-redraw` never fires). The
 *    nette.ajax `before`/`complete`/`success` triple is the only hook.
 *  - nette.ajax extension names must be unique or it throws — and a duplicate silently
 *    replaces the first registration. The mailing overlay is a plain
 *    `window.fiMailingOverlay` callback invoked from here for that reason.
 *  - `before` must not return `false`: nette.ajax aborts the request on a falsy return
 *    from any extension callback.
 *  - `--fi-sticky-top` is synced from the real `.topbar` bottom edge, not hardcoded,
 *    because the impersonation banner pushes the topbar down and the sticky head would
 *    hide under it.
 *  - No user-facing text here — translated strings arrive via `window.fiGridLang`.
 *
 * Grid conventions (docs/frontend/design-system.md) implemented here: two-line cells
 * (`.fi-cell` + a tooltip when clipped), the ⋯ row menu (`.fi-row-menu`) and the scroll
 * width the Ublaboo footer pins itself to.
 */
(function (window, document, $) {
    "use strict";

    if (typeof $ !== "function" || !$.fn) {
        return;
    }

    var STICKY_VAR = "--fi-sticky-top";
    var WRAP = "fi-table-wrap";
    var BUSY = "fi-busy";
    var CELL = "fi-cell";
    // A DataTables cell holding one of these keeps its natural height: clipping would cut off
    // a menu, a form control or a checkbox.
    var CELL_KEEP = ".fi-row-menu, .dropdown, .btn-group, input, select, textarea, button";

    /** Reads a --fi-* custom property off <html>, with a literal fallback. */
    function cssVar(name, fallback) {
        var value = window.getComputedStyle(document.documentElement).getPropertyValue(name);
        return value ? value.trim() : fallback;
    }

    function escapeAttr(value) {
        return String(value)
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    }

    /**
     * Publishes the height of the fixed chrome as `--fi-sticky-top` so sticky table
     * heads park right below it (70px normally, more with the impersonation banner).
     */
    function fiSyncStickyTop() {
        var topbar = document.querySelector(".topbar");

        if (!topbar) {
            return;
        }

        var bottom = Math.round(topbar.getBoundingClientRect().bottom);
        document.documentElement.style.setProperty(STICKY_VAR, Math.max(0, bottom) + "px");
    }

    /**
     * Gives every `table.fi-table` a scroll shell and keeps its state classes in
     * sync. DataTables builds its own scroll chrome, so its tables are skipped.
     * `is-windowed` is opt-in through `data-fi-window` on the table.
     * `--fi-wrap-w` is the visible width: the Ublaboo footer pins itself to it, so the
     * per-page select and pages stay in view on a table wider than the page.
     */
    function fiSyncWraps(root) {
        $(root || document)
            .find("table.fi-table")
            .each(function () {
                var $table = $(this);

                if ($table.hasClass("dataTable")) {
                    return;
                }

                var $wrap = $table.parent("." + WRAP);

                if (!$wrap.length) {
                    $table.wrap('<div class="' + WRAP + '"></div>');
                    $wrap = $table.parent();
                }

                $wrap.toggleClass("is-scrollable", this.offsetWidth > $wrap[0].clientWidth + 1);
                $wrap.toggleClass("is-windowed", $table.is("[data-fi-window]"));
                $wrap[0].style.setProperty("--fi-wrap-w", $wrap[0].clientWidth + "px");
                fiSyncClip($wrap[0]);
            });
    }

    /**
     * Publishes the width of a page-level action button floating over a grid's toolbar row
     * as `--fi-page-actions-w` on its card body. theme/_nav.scss reserves that much room on
     * the right of the toolbar, so the column gear stays right-aligned and lands just left
     * of the button instead of being flipped to the other end of the row.
     */
    function fiSyncPageActions(root) {
        $(root || document)
            .find(".page-actions--grid")
            .each(function () {
                var card = this.parentElement;

                if (card) {
                    card.style.setProperty("--fi-page-actions-w", this.offsetWidth + "px");
                }
            });
    }

    /**
     * A DataTables grid renders its own search band, so a page action marked for the grid
     * moves into it (right of the search field) instead of floating in a strip of its own.
     * Ublaboo grids keep the float: their toolbar row reserves its width instead.
     */
    function fiSyncDtActions(root) {
        $(root || document)
            .find(".dataTables_wrapper > .row.fi-dt-toolbar")
            .each(function () {
                var $band = $(this);
                var $actions = $band
                    .closest(".card-body, .card, form, body")
                    .find(".page-actions--grid")
                    .first();

                if ($actions.length && !$actions.parent().is($band)) {
                    $band.append($actions);
                }
            });
    }

    /** `is-clipped-right` = columns still hidden past the right edge; draws the sticky action column's shadow. */
    function fiSyncClip(wrap) {
        wrap.classList.toggle("is-clipped-right", wrap.scrollLeft + wrap.clientWidth < wrap.scrollWidth - 1);
    }

    /** Bootstrap 4 tooltips for markup that arrived after page load. */
    function fiTooltips(root) {
        if (!$.fn.tooltip) {
            return;
        }

        $(root || document)
            .find('[data-toggle="tooltip"]')
            .each(function () {
                var $el = $(this);

                if ($el.data("bs.tooltip")) {
                    return;
                }

                $el.tooltip({ container: "body", trigger: "hover" });
            });
    }

    /** select2 on grid filter selects; already-enhanced elements are left alone. */
    function fiGridSelects() {
        if (!$.fn.select2) {
            return;
        }

        var $selects = $(".datagrid select.select2, .datagrid .js-select2").not(
            ".select2-hidden-accessible"
        );

        if ($selects.length) {
            $selects.select2({ width: "100%" });
        }
    }

    // --- Two-line cells -------------------------------------------------------------

    /**
     * Wraps the content of every DataTables body cell in `.fi-cell` once. Runs after
     * DataTables has cached the cell data (init / draw), so sorting, search and
     * data-order never see the wrapper. Returns whether anything was wrapped.
     */
    function fiWrapDtCells(api) {
        var wrapped = false;

        $(api.table().body())
            .children("tr")
            .children("td")
            .each(function () {
                if (
                    this.classList.contains("dataTables_empty") ||
                    this.classList.contains("fi-cell-free") ||
                    $(this).children("." + CELL).length ||
                    !this.firstChild ||
                    $(this).find(CELL_KEEP).length
                ) {
                    return;
                }

                var cell = document.createElement("div");
                cell.className = CELL;

                while (this.firstChild) {
                    cell.appendChild(this.firstChild);
                }

                this.appendChild(cell);
                wrapped = true;
            });

        return wrapped;
    }

    /** A cell cut at two lines shows its full text on hover. */
    function fiMarkClipped(root) {
        $(root || document)
            .find("." + CELL)
            .each(function () {
                if (this.hasAttribute("title")) {
                    return;
                }

                if (this.scrollHeight > this.clientHeight + 1 || this.scrollWidth > this.clientWidth + 1) {
                    var text = (this.innerText || this.textContent || "").replace(/\s+/g, " ").trim();

                    if (text) {
                        this.setAttribute("title", text);
                        this.setAttribute("data-toggle", "tooltip");
                    }
                }
            });
    }

    // --- Row menu -------------------------------------------------------------------

    /**
     * The ⋯ menu stays inside its row (row-scoped handlers such as `closest('table')` keep
     * working), so a scroll container would clip it. Fixed positioning escapes the clip;
     * the toggle is configured on first contact, before Bootstrap's click handler creates
     * the dropdown with defaults. A modal keeps the default: `.modal-right` is transformed,
     * which would offset a fixed menu.
     */
    function fiRowMenuInit() {
        var $toggle = $(this);

        if (!$.fn.dropdown || $toggle.data("bs.dropdown") || $toggle.closest(".modal").length) {
            return;
        }

        $toggle.dropdown({ popperConfig: { positionFixed: true } });
    }

    /**
     * Same markup as components/grid/rowMenu.latte and App\Helpers\GridRowMenu, for rows
     * rendered in JS (DataTables `columns.render`). `itemsHtml` are ready `.dropdown-item`
     * strings — escape their values where they are built.
     */
    function fiRowMenu(itemsHtml, label) {
        var items = [].concat(itemsHtml).filter(Boolean);

        if (!items.length) {
            return "";
        }

        var title = escapeAttr(label || "");

        return (
            '<div class="dropdown fi-row-menu">' +
            '<button type="button" class="table-action-btn fi-row-menu__toggle" data-toggle="dropdown"' +
            ' aria-haspopup="true" aria-expanded="false" aria-label="' + title + '" title="' + title + '">' +
            '<i class="fas fa-ellipsis-h"></i></button>' +
            '<div class="dropdown-menu dropdown-menu-right">' + items.join("") + "</div>" +
            "</div>"
        );
    }

    // --- Collapsed list cells ---------------------------------------------------------

    /**
     * Collapsed list cells („Účastníci: 4"): the items open in a popover on hover, or on
     * click/tap through focus (the badge carries tabindex="0"). Created on first contact, so
     * no redraw needs a re-init. data-content is server-escaped HTML (items joined by <br>)
     * and still passes Bootstrap's sanitizer.
     */
    function fiCountPopover() {
        var $el = $(this);

        if (!$.fn.popover || $el.data("bs.popover")) {
            return;
        }

        $el.popover({
            trigger: "hover focus",
            container: "body",
            placement: "top",
            html: true,
            customClass: "fi-count-popover",
        });
        $el.popover("show");
    }

    /** A redraw removes the badge but not its popover, which lives on <body>. */
    function fiDropCountPopovers() {
        $(".popover.fi-count-popover").remove();
    }

    function fiRefresh() {
        fiDropCountPopovers();
        fiSyncDtActions(document);
        fiSyncPageActions(document);
        fiSyncWraps(document);
        fiMarkClipped(document);
        fiTooltips(document);
        fiGridSelects();

        if (typeof window.fiMailingOverlay === "function") {
            window.fiMailingOverlay();
        }
    }

    // --- Vendor defaults ----------------------------------------------------------

    if ($.fn.selectpicker && $.fn.selectpicker.Constructor) {
        // Rendered on <body> so the menu escapes the grid's scroll container.
        $.fn.selectpicker.Constructor.DEFAULTS.container = "body";
        $.fn.selectpicker.Constructor.DEFAULTS.style = "btn-light fi-select";
    }

    if ($.fn.dataTable && $.fn.dataTable.Buttons) {
        // Musí to být skutečná varianta z theme/_buttons.scss. Dřív tu bylo "fi-btn", což není
        // žádné pravidlo – tlačítka zůstala na holém .btn, které má napevno color:#1c1c1c, takže
        // v tmavém režimu byla černá na černé. .btn-outline-secondary jede na tokenech (--fi-line-strong,
        // --fi-text-2) a jedna třída tak obslouží obě témata.
        $.fn.dataTable.Buttons.defaults.dom.button.className = "btn btn-sm btn-outline-secondary";
    }

    if ($.fn.datepicker && $.fn.datepicker.defaults) {
        $.fn.datepicker.defaults.language = document.documentElement.lang || "cs";
    }

    // --- AJAX lifecycle -----------------------------------------------------------

    if ($.nette && typeof $.nette.ext === "function") {
        $.nette.ext("fi-tables", {
            before: function (xhr, settings) {
                var el = settings && settings.nette ? settings.nette.el : null;

                if (el && el.length) {
                    el.closest(".datagrid").addClass(BUSY);
                }
            },
            complete: function () {
                $("." + BUSY).removeClass(BUSY);
            },
            success: function () {
                fiRefresh();
            },
        });
    }

    // --- DataTables ---------------------------------------------------------------

    /**
     * Toolbar / footer bands come from `dom` (default in @layout.latte). SearchPanes builds
     * DataTables of its own inside the panes container; those are not grids and are skipped.
     */
    function fiDtCells(settings) {
        if (!$.fn.dataTable || $(settings.nTable).closest(".dtsp-panesContainer").length) {
            return;
        }

        var api = new $.fn.dataTable.Api(settings);

        if (fiWrapDtCells(api) && settings.oScroll && settings.oScroll.sX) {
            // The scrollX header is a separate table; realign it with the wrapped body.
            api.columns.adjust();
        }

        fiMarkClipped(api.table().body());
    }

    $(document).on("init.dt", function (event, settings) {
        if (!$.fn.dataTable) {
            return;
        }

        var container = new $.fn.dataTable.Api(settings).table().container();

        $(container).addClass("fi-dt");
        fiDtCells(settings);
        // The bands only exist once the table is initialised, which happens after fiRefresh.
        fiSyncDtActions(container);
    });

    $(document).on("draw.dt", function (event, settings) {
        fiDropCountPopovers();
        fiDtCells(settings);
        fiTooltips(document);
        fiSyncDtActions(document);
        fiSyncWraps(document);
    });

    $(document).on("mouseenter focusin", ".js-count-popover", fiCountPopover);
    $(document).on("mousedown focusin", ".fi-row-menu__toggle", fiRowMenuInit);

    // scroll does not bubble, so the one document-level listener has to capture it.
    document.addEventListener("scroll", function (event) {
        var el = event.target;

        if (el.classList && el.classList.contains(WRAP)) {
            fiSyncClip(el);
        }
    }, { capture: true, passive: true });

    // A grid inside a hidden tab pane measures zero, so the toolbar reserves nothing and the
    // column gear ends up under the page action button. Measure again once the pane is shown.
    $(document).on("shown.bs.tab", function () {
        fiSyncPageActions(document);
        fiSyncWraps(document);
    });

    // A wider or narrower window can add or remove the overflow itself, not only its edge.
    var resizeFrame = 0;

    window.addEventListener("resize", function () {
        if (resizeFrame) {
            return;
        }

        resizeFrame = window.requestAnimationFrame(function () {
            resizeFrame = 0;
            fiSyncPageActions(document);
            fiSyncWraps(document);
        });
    }, { passive: true });

    // --- Ublaboo action confirmation ----------------------------------------------

    $(document).on("click", ".datagrid-swal", function (event) {
        event.preventDefault();
        event.stopPropagation();

        var $el = $(this);
        var url = $el.data("swal-href") || $el.attr("href");
        var lang = window.fiGridLang || {};

        function run() {
            $.nette.ajax({ url: url });
        }

        if (typeof window.Swal === "undefined") {
            if (window.confirm($el.data("swal-confirm-title") || lang.confirm || "")) {
                run();
            }

            return;
        }

        window.Swal
            .fire({
                title: $el.data("swal-confirm-title") || lang.confirm,
                text: $el.data("swal-confirm-text") || "",
                icon: $el.data("swal-type") || "info",
                showCancelButton: true,
                confirmButtonText: $el.data("swal-confirm-button-text") || lang.yes,
                cancelButtonText: $el.data("swal-cancel-button-text") || lang.no,
                confirmButtonColor: cssVar("--fi-gold", "#c9963b"),
                cancelButtonColor: cssVar("--fi-slate", "#3f4654"),
            })
            .then(function (result) {
                if (result.value) {
                    run();
                }
            });
    });

    // --- Row click -------------------------------------------------------------------

    // A click that lands on (or inside) one of these already has its own target — a link,
    // a form control, a popover/badge trigger, the ⋯ menu — so the row must not also navigate.
    var ROW_CLICK_IGNORE =
        'a, button, input, select, textarea, label, [role="button"], [data-toggle], ' +
        ".fi-row-menu, .dropdown-menu, .dtr-control, .fi-no-row-click";

    /**
     * The record name stays the *real* link (`a.fi-row-link`, set server-side): that keeps
     * middle-click, Ctrl/⌘-click, keyboard focus and per-link ACL gating free. This handler
     * only forwards a click anywhere else in the row to that link — a row without the marker
     * is left alone (no link to forward to).
     */
    $(document).on("click auxclick", "table > tbody > tr", function (event) {
        if (event.type === "auxclick" ? event.button !== 1 : event.button !== 0) {
            return;
        }

        if (event.target.closest(ROW_CLICK_IGNORE)) {
            return;
        }

        var $cell = $(event.target).closest("td");

        if ($cell.length && $cell.find('input[type="checkbox"]').length) {
            return;
        }

        if (window.getSelection().toString() !== "") {
            return;
        }

        var link =
            this.querySelector("a.fi-row-link:not(.fi-row-link--fallback)") ||
            this.querySelector("a.fi-row-link");

        if (!link || link.closest("tr") !== this) {
            return;
        }

        var openNewTab = event.type === "auxclick" || event.ctrlKey || event.metaKey || event.shiftKey;

        if (openNewTab && !link.classList.contains("ajax")) {
            event.preventDefault();
            window.open(link.href, "_blank", "noopener");
            return;
        }

        event.preventDefault();
        link.click();
    });

    // --- Boot ---------------------------------------------------------------------

    $(window).on("resize", fiSyncStickyTop);
    $(window).on("load", fiSyncStickyTop);

    $(function () {
        fiSyncStickyTop();
        fiRefresh();
    });

    window.fiSyncStickyTop = fiSyncStickyTop;
    window.fiSyncWraps = fiSyncWraps;
    window.fiSyncPageActions = fiSyncPageActions;
    window.fiSyncDtActions = fiSyncDtActions;
    window.fiTooltips = fiTooltips;
    window.fiRowMenu = fiRowMenu;
})(window, document, window.jQuery);
